import React from 'react';
import { Button } from '@/components/ui/button';
import { LogOut, User, Menu, Sun, Moon, Bell, Check, Trash2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useAppConfig } from '@/contexts/AppConfigContext';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '@/contexts/NotificationContext';
import VoiceCommandBar from '@/components/Copilot/VoiceCommandBar';
import GlobalSearch from '@/components/Layout/GlobalSearch';
import { Search } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { format } from 'date-fns';

interface HeaderProps {
  toggleSidebar: () => void;
}

const Header: React.FC<HeaderProps> = ({ toggleSidebar }) => {
  const { user, signOut } = useAuth();
  const { darkMode, toggleDarkMode } = useAppConfig();
  const { t } = useTranslation();
  const { notifications, unreadCount, markAsRead, markAllAsRead, removeNotification } = useNotifications();
  const [searchOpen, setSearchOpen] = React.useState(false);

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <header className="bg-transparent px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleSidebar}
            className="text-muted-foreground hover:text-foreground"
          >
            <Menu className="w-5 h-5" />
          </Button>
          <div className="flex items-center lg:hidden">
            <img 
              src="/logo.png" 
              alt="Bassline Logo" 
              className="h-9 w-auto max-w-[180px] object-contain"
              onError={(e) => {
                const textElement = document.createElement('div');
                textElement.className = 'text-xl font-bold text-crm-primary';
                textElement.textContent = 'Bassline CRM';
                (e.currentTarget as HTMLImageElement).parentNode?.replaceChild(textElement, e.currentTarget);
              }}
            />
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            className="hidden md:flex relative h-9 w-60 justify-start rounded-[0.5rem] bg-muted/50 text-sm font-normal text-muted-foreground shadow-none sm:pr-12 md:w-40 lg:w-64"
            onClick={() => setSearchOpen(true)}
          >
            <span className="hidden lg:inline-flex">Buscar clientes, eventos...</span>
            <span className="inline-flex lg:hidden">Buscar...</span>
            <kbd className="pointer-events-none absolute right-[0.3rem] top-[0.3rem] hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
              <span className="text-xs">⌘</span>K
            </kbd>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="md:hidden h-9 w-9 p-0 text-muted-foreground hover:text-foreground"
            onClick={() => setSearchOpen(true)}
          >
            <Search className="h-5 w-5" />
          </Button>

          <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />

          <div className="hidden md:block">
            <VoiceCommandBar />
          </div>
          {/* Notifications Bell */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="relative h-9 w-9 p-0 text-muted-foreground hover:text-foreground">
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <Badge 
                    className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-crm-primary text-white border-2 border-background animate-pulse"
                  >
                    {unreadCount}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 p-0 bg-card border-border shadow-xl">
              <DropdownMenuLabel className="p-4 flex items-center justify-between font-bold">
                <span className="flex items-center">
                  <Bell className="w-4 h-4 mr-2 text-crm-primary" />
                  {t('notifications.title')}
                </span>
                {unreadCount > 0 && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={(e) => {
                      e.stopPropagation();
                      markAllAsRead();
                    }}
                    className="h-auto text-[10px] px-2 py-1 text-crm-primary hover:text-crm-primary hover:bg-crm-primary/10"
                  >
                    {t('notifications.mark_all_read')}
                  </Button>
                )}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <ScrollArea className="h-[350px]">
                {notifications.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <Bell className="w-12 h-12 mx-auto mb-3 opacity-20" />
                    <p className="text-sm">{t('notifications.empty')}</p>
                  </div>
                ) : (
                  <div className="flex flex-col">
                    {notifications.map((notification) => (
                      <div 
                        key={notification.id}
                        className={`p-4 border-b border-border transition-colors hover:bg-muted/50 group relative ${!notification.isRead ? 'bg-primary/5' : ''}`}
                      >
                        <div className="flex justify-between items-start mb-1">
                          <h4 className={`text-sm font-semibold truncate pr-8 ${!notification.isRead ? 'text-foreground' : 'text-muted-foreground'}`}>
                            {notification.title}
                          </h4>
                          <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                            {format(notification.createdAt, 'HH:mm')}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-2 pr-6">
                          {notification.message}
                        </p>
                        
                        <div className="absolute top-4 right-2 flex flex-col space-y-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          {!notification.isRead && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                markAsRead(notification.id);
                              }}
                              className="h-6 w-6 p-0 text-crm-primary hover:bg-crm-primary/10"
                              title={t('notifications.mark_as_read')}
                            >
                              <Check className="h-3 w-3" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeNotification(notification.id);
                            }}
                            className="h-6 w-6 p-0 text-destructive hover:bg-destructive/10"
                            title={t('notifications.delete')}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
              <DropdownMenuSeparator />
              <div className="p-2">
                <Button variant="ghost" size="sm" className="w-full text-xs text-muted-foreground hover:text-crm-primary">
                  {t('notifications.view_all')}
                </Button>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            variant="ghost"
            size="sm"
            onClick={toggleDarkMode}
            className="text-muted-foreground hover:text-foreground h-9 w-9 p-0"
            title={darkMode ? t('common.light_mode') : t('common.dark_mode')}
          >
            {darkMode ? (
              <Sun className="h-5 w-5 rotate-0 scale-100 transition-all" />
            ) : (
              <Moon className="h-5 w-5 rotate-0 scale-100 transition-all" />
            )}
            <span className="sr-only">Toggle theme</span>
          </Button>


          
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSignOut}
            className="text-muted-foreground hover:text-foreground"
          >
            <LogOut className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">{t('common.logout')}</span>
          </Button>
        </div>
      </div>
    </header>
  );
};

export default Header;
